"use client"

import styled from "@emotion/styled"
import {
  commonSchema,
  CustomCheckbox,
  CustomHelperText,
  CustomTextField,
  ReCaptchaDisclaimer,
} from "@eurodrive/ui"
import { yupResolver } from "@hookform/resolvers/yup"
import { Button, Typography } from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import {
  getReCaptchaToken,
  loadReCaptcha,
} from "@eurodrive/utils/recaptcha"
import { postCareerJobApplication } from "api"
import Image from "next/image"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import * as yup from "yup"
import { fetchJobApplicationRecipient } from "./fetchJobApplicationRecipient"
import Result from "./Result"

const OuterLayout = styled.div`
  display: flex;
`
const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding: 20px;

  height: fit-content;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 32px;
    border: 1px solid #aaaaaa;
    border-radius: 8px;
  }
`

const InputRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  flex-direction: row;
`

const FileUploadContainer = styled.div`
  border: 1px dashed #aaaaaa;
  border-radius: 8px;
  padding: 13px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
  position: relative;
`

const FileUploadLabel = styled.div`
  position: absolute;
  top: -1.5px;
  left: 10px;
  background-color: white;
  padding: 0 4px;
  height: 2px;
  display: flex;
  align-items: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    right: 296px;
  }
  /* width: max-content; */
`

const FileUploadContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const FileUploadDescription = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 0 16px;
  width: 100%;
`

const SubmitButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 40px;
  width: 100%;
`

const UploadedFileContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 4px;
`

const FileInfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: white;
  padding: 12px;
  border-radius: 4px;
`

const FileDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

const FileDetailsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const FileSizeDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #6f6f6f;
`

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`

// Add file upload to the schema
const schema = yup.object({
  firstName: commonSchema.common("he").firstName,
  lastName: commonSchema.common("he").lastName,
  email: commonSchema.common("he").email,
  phone: commonSchema.common("he").phone,
  city: yup.string().required("עליך לבחור עיר מגורים"),
  resume: yup
    .mixed<File>()
    .nullable()
    .test("fileRequired", "יש להעלות קובץ קורות חיים", (value) => {
      return value instanceof File
    }),
  termsAgreement: commonSchema.common("he").termsAgreement,
})

type FormData = yup.InferType<typeof schema>

export default function CareerForm({
  jobTitle,
  jobNumber,
  jobCity,
  jobCategory,
  locale,
}: {
  jobTitle: string
  jobNumber: string
  jobCity: string
  jobCategory: string
  locale: string
}) {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">(
    "idle",
  )

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitted, isSubmitting },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  })

  const resume = watch("resume")

  // Preload reCAPTCHA so submit is instant (the badge itself is hidden)
  useEffect(() => {
    loadReCaptcha()
  }, [])

  const mutation = useMutation({
    mutationFn: postCareerJobApplication,
    onSuccess: (response) => {
      // API returns HTTP 200 even for validation errors — check errorCode/errorMessage
      const { errorCode, errorMessage, body } = response.data || {}
      if (errorCode !== 0 || errorMessage) {
        console.error("API validation error:", errorMessage)
        setFormStatus("error")
        return
      }
      // A clean response with body:false means the send itself failed on the
      // server (SMTP). Telling the candidate their application went through
      // would be wrong — it never left the building.
      if (body === false) {
        console.error("Application was not sent by the server")
        setFormStatus("error")
        return
      }
      setFormStatus("success")
      // Reset form on success
      reset()
      // Reset file input as well
      const fileInput = document.getElementById(
        "resume-upload",
      ) as HTMLInputElement
      if (fileInput) {
        fileInput.value = ""
      }
    },
    onError: (error) => {
      console.error("Failed to submit application:", error)
      setFormStatus("error")
    },
  })

  const onSubmit = async (data: FormData) => {
    // Fresh token per submit; server verifies it (action must match exactly).
    // The recipient is read live here too, not from the cached page (END-318).
    const [reCaptchaToken, recipientEmail] = await Promise.all([
      getReCaptchaToken("job_application"),
      fetchJobApplicationRecipient(locale),
    ])

    // Create FormData for the API call
    const apiFormData = new FormData()
    apiFormData.append("FirstName", data.firstName)
    apiFormData.append("LastName", data.lastName)
    apiFormData.append("EmailAddress", data.email)
    apiFormData.append("PhoneNumber", data.phone)
    apiFormData.append("CandidateArea", data.city)
    apiFormData.append("JobName", jobTitle)
    apiFormData.append("JobId", jobNumber)
    apiFormData.append("JobLocation", jobCity)
    apiFormData.append("JobCategory", jobCategory)
    if (data.resume) {
      apiFormData.append("Resume", data.resume)
    }
    // Optional on the API (END-307): when the Umbraco field is unset the
    // parameter must be left out entirely so the server applies its own
    // default recipient. Sending a placeholder here would suppress that.
    if (recipientEmail) {
      apiFormData.append("RecipientEmailAddress", recipientEmail)
    }
    apiFormData.append("ReCaptchaToken", reCaptchaToken)

    mutation.mutate(apiFormData)
  }

  const handleRetry = () => {
    setFormStatus("idle")
  }

  // Show result component if form was submitted
  if (formStatus === "success" || formStatus === "error") {
    return (
      <Result
        type={formStatus}
        onRetry={formStatus === "error" ? handleRetry : undefined}
      />
    )
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setValue("resume", file, { shouldValidate: true })
    }
  }

  const handleFileDelete = () => {
    setValue("resume", null, { shouldValidate: true })
    // Also reset the file input
    const fileInput = document.getElementById(
      "resume-upload",
    ) as HTMLInputElement
    if (fileInput) {
      fileInput.value = ""
    }
  }

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)} aria-label="טופס הגשת מועמדות">
      {/* Name fields row */}
      <InputRow>
        <CustomTextField
          name="firstName"
          control={control}
          label="שם פרטי*"
          placeholder="לדוגמה: שחר"
          required
          errors={errors}
          isSubmitted={isSubmitted}
          slotProps={{
            htmlInput: { autoComplete: "given-name" },
          }}
        />
        <CustomTextField
          name="lastName"
          control={control}
          label="שם משפחה*"
          placeholder="לדוגמה: לוי"
          required
          errors={errors}
          isSubmitted={isSubmitted}
          slotProps={{
            htmlInput: { autoComplete: "family-name" },
          }}
        />
      </InputRow>

      {/* Contact fields row */}
      <InputRow>
        <CustomTextField
          name="email"
          control={control}
          label="דוא״ל*"
          placeholder="example@gmail.com"
          required
          errors={errors}
          isSubmitted={isSubmitted}
          slotProps={{
            htmlInput: { autoComplete: "email" },
          }}
        />
        <CustomTextField
          name="phone"
          control={control}
          label="מספר טלפון*"
          placeholder="לדוגמה: 058-123-4567"
          required
          errors={errors}
          isSubmitted={isSubmitted}
          slotProps={{
            htmlInput: { autoComplete: "tel-national" },
          }}
        />
      </InputRow>

      {/* City select */}
      <CustomTextField
        name="city"
        control={control}
        label="עיר מגורים*"
        placeholder="בחר עיר"
        required
        errors={errors}
        isSubmitted={isSubmitted}
      />

      {/* File upload */}
      <FileUploadContainer>
        <FileUploadLabel>
          <Typography
            variant="caption"
            sx={{
              fontSize: "12px",
              color: "#000000",
              letterSpacing: "0.15%",
            }}
          >
            העלה קובץ קורות חיים *
          </Typography>
        </FileUploadLabel>

        {!resume ? (
          <FileUploadContent>
            <Image src={"/icons/upload.svg"} width={32} height={32} alt="" aria-hidden="true" />

            <FileUploadDescription id="resume-upload-description">
              <Typography
                variant="body1"
                sx={{
                  fontSize: "16px",
                  lineHeight: "1.5em",
                  color: "#000000",
                }}
              >
                בחר קובץ קורות חיים
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  lineHeight: "1.5em",
                  color: "#6F6F6F",
                }}
              >
                ניתן להעלות קובצי קו"ח בפורמטים PDF/ WORD בלבד
              </Typography>
            </FileUploadDescription>

            <input
              type="file"
              id="resume-upload"
              accept=".pdf,.doc,.docx"
              onChange={handleFileUpload}
              style={{ display: "none" }}
              aria-label="העלה קובץ קורות חיים"
              aria-describedby="resume-upload-description"
            />
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => document.getElementById("resume-upload")?.click()}
              aria-label="העלה קובץ קורות חיים"
              aria-required="true"
              aria-invalid={!!errors.resume}
              aria-describedby={`resume-upload-description${errors.resume && isSubmitted ? " resume-error" : ""}`}
            >
              בחר קובץ
            </Button>
          </FileUploadContent>
        ) : (
          <FileUploadContent>
            <Image src={"/icons/upload.svg"} width={32} height={32} alt="" aria-hidden="true" />

            <FileUploadDescription>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "16px",
                  lineHeight: "1.5em",
                  color: "#000000",
                }}
              >
                בחר קובץ קורות חיים
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  lineHeight: "1.5em",
                  color: "#6F6F6F",
                }}
              >
                ניתן להעלות קובצי קו"ח בפורמטים PDF/ WORD בלבד
              </Typography>
            </FileUploadDescription>

            {/* Uploaded file display */}
            <UploadedFileContainer>
              <FileInfoContainer>
                <FileDetailsContainer>
                  <FileDetailsRow>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        lineHeight: "1.31em",
                        color: "#1E1F22",
                      }}
                    >
                      {((resume as File).size / (1024 * 1024)).toFixed(1)}MB
                    </Typography>
                    <FileSizeDot />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        lineHeight: "1.31em",
                        color: "#000000",
                      }}
                    >
                      {(resume as File).name}
                    </Typography>
                  </FileDetailsRow>
                </FileDetailsContainer>

                <Image src="/icons/pdf.svg" width={24} height={24} alt="PDF" />
              </FileInfoContainer>
              <DeleteButton onClick={handleFileDelete}>
                <Image
                  src="/icons/trash.svg"
                  width={24}
                  height={24}
                  alt="מחק קובץ"
                />
              </DeleteButton>
            </UploadedFileContainer>
          </FileUploadContent>
        )}

        {/* Show error if resume is not uploaded */}
        {errors.resume && (
          <CustomHelperText
            render={isSubmitted}
            error={!!errors.resume}
            text={errors.resume.message}
            id="resume-error"
          />
        )}
      </FileUploadContainer>

      {/* Terms agreement checkbox */}
      <CheckboxContainer>
        <CustomCheckbox
          name="termsAgreement"
          control={control}
          required
          isSubmitted={isSubmitted}
          errors={errors}
          label="אישור שימוש בפרטים ושמירה במאגר של יורודרייב"
        />
      </CheckboxContainer>

      {/* Submit button */}
      <SubmitButtonContainer>
        <Button
          variant="primary"
          size="large"
          type="submit"
          fullWidth
          // isSubmitting covers the reCAPTCHA/recipient lookups before mutate
          // starts; without it a second click there sends a duplicate.
          disabled={isSubmitting || mutation.isPending}
        >
          שליחה
        </Button>
      </SubmitButtonContainer>

      <ReCaptchaDisclaimer />
    </StyledForm>
  )
}
